import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  // IconButton
} from '@mui/material';
import {
  Assignment,
  TrendingUp,
  AttachMoney,
  AccessTime,
  Star,
  Notifications
} from '@mui/icons-material';
import { useResponsive } from '../../hooks/useResponsive';
import { useAuth } from '../../contexts/AuthContext';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactElement;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, trend }) => {
  // const { isMobile } = useResponsive();

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Avatar sx={{ backgroundColor: color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
          {trend && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TrendingUp 
                sx={{ 
                  fontSize: 16, 
                  color: trend.isPositive ? 'success.main' : 'error.main',
                  transform: trend.isPositive ? 'none' : 'rotate(180deg)'
                }} 
              />
              <Typography 
                variant="caption" 
                sx={{ 
                  color: trend.isPositive ? 'success.main' : 'error.main',
                  fontWeight: 'bold'
                }}
              >
                {trend.isPositive ? '+' : ''}{trend.value}%
              </Typography>
            </Box>
          )}
        </Box>
        
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          {value}
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );
};

export const ExecutorDashboard: React.FC = () => {
  // const { isMobile } = useResponsive();
  const { state } = useAuth();

  const user = state.user?.user;

  // Моковые данные для демонстрации
  const stats = [
    {
      title: 'Активные задачи',
      value: 3,
      icon: <Assignment />,
      color: '#2196f3',
      trend: { value: 12, isPositive: true }
    },
    {
      title: 'Заработано в месяце',
      value: '₽15,420',
      icon: <AttachMoney />,
      color: '#4caf50',
      trend: { value: 8, isPositive: true }
    },
    {
      title: 'Рейтинг',
      value: '4.8',
      icon: <Star />,
      color: '#ff9800',
    },
    {
      title: 'Часов в неделю',
      value: 18,
      icon: <AccessTime />,
      color: '#9c27b0',
      trend: { value: 5, isPositive: false }
    }
  ];

  const recentTasks = [
    {
      id: 1,
      title: 'Создать дизайн логотипа',
      organization: 'ООО "Инновации"',
      status: 'В работе',
      deadline: '2024-01-25',
      payment: '₽3,000',
      progress: 75
    },
    {
      id: 2,
      title: 'Написать статью о IT-трендах',
      organization: 'Медиа Хаус',
      status: 'На проверке',
      deadline: '2024-01-23',
      payment: '₽5,500',
      progress: 100
    },
    {
      id: 3,
      title: 'Сделать презентацию',
      organization: 'Колледж №15',
      status: 'Новая',
      deadline: '2024-01-30',
      payment: '₽2,200',
      progress: 0
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'В работе': return 'primary';
      case 'На проверке': return 'warning';
      case 'Новая': return 'secondary';
      case 'Завершена': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Привет, {user?.phone ? `+${user.phone}` : 'Исполнитель'}! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Вот обзор вашей активности на сегодня
        </Typography>
      </Box>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatCard {...stat} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Активные задачи */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Мои задачи
                </Typography>
                <Button variant="outlined" size="small">
                  Все задачи
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {recentTasks.map((task) => (
                  <Card 
                    key={task.id} 
                    variant="outlined"
                    sx={{ 
                      '&:hover': { 
                        boxShadow: 2,
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {task.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {task.organization}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Chip 
                              label={task.status} 
                              size="small" 
                              color={getStatusColor(task.status) as any}
                            />
                            <Typography variant="caption" color="text.secondary">
                              До {new Date(task.deadline).toLocaleDateString('ru-RU')}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ textAlign: 'right', ml: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                            {task.payment}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {task.progress}% готово
                          </Typography>
                        </Box>
                      </Box>

                      {task.progress > 0 && (
                        <LinearProgress 
                          variant="determinate" 
                          value={task.progress} 
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Боковая панель */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Быстрые действия */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Быстрые действия
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button 
                    variant="contained" 
                    fullWidth 
                    startIcon={<Assignment />}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Найти новые задачи
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Star />}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Мои навыки
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<AttachMoney />}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Заработок
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Уведомления */}
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Уведомления
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, backgroundColor: 'primary.main' }}>
                      <Notifications sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        Новая задача
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Для вас доступна новая задача по дизайну
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, backgroundColor: 'success.main' }}>
                      <AttachMoney sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        Выплата готова
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ₽5,500 готовы к выводу
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
